import Logger from '@utils/log'
import { useRef, useState, useMemo, useCallback } from 'react'
import config from '@/config'
import { useWire, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import { looksLikeUuid, buildRecordUrl } from '@utils'
import * as canvasUtils from '@utils/canvas'
import * as elasticUtils from '@utils/elasticsearch'

const log = new Logger('DocumentViewerViewModel')

const highlightThemes = {
    parse: {
        fillStyle: 'rgba(0, 177, 171, 0.25)', // parse blue color
        strokeStyle: 'rgba(0, 177, 171, 0.80)', // parse blue color but with more opacity
    },
    classic: {
        fillStyle: 'rgba(255, 255, 30, 0.25)',
        strokeStyle: 'rgba(45, 45, 30, 0.80)',
    },
}

// eslint-disable-next-line no-restricted-globals
const createCanvas = () => document.createElement('canvas')

/**
 * DocumentViewerV2 ViewModel
 * 
 * @param {Record} record Document that matched a search query
 * @param {Boolean} loading Let this view model know if we're still loading the PDF
 * @param {Number} pageIndex Current document page index
 * @constructor
 */
const DocumentViewerViewModel = ({
    record,
    pageIndex,
}) => {
    
    //region Refs
    
    /**
     * Main canvas displayed on the screen
     * @type {React.MutableRefObject<HTMLCanvasElement>}
     */
    const refRenderCanvas = useRef()
    
    /**
     * Off-screen document canvas used by PDF renderer
     * @type {React.MutableRefObject<HTMLCanvasElement>}
     */
    const refDocumentCanvas = useRef(createCanvas())
    
    /**
     * Off-screen overlay canvas for highlighted search terms
     * @type {React.MutableRefObject<HTMLCanvasElement>}
     */
    const refOverlaysCanvas = useRef(createCanvas())
    
    //endregion Refs
    
    //region State
    
    /**
     * Exported to allow the PDF renderer to let us know if therw was an error while loading the document
     * @type {String|null}
     */
    const [error, setError] = useState(null)
    
    /**
     * Exported to allow the PDF renderer to let us know when it's finished loading the document
     * @type {Boolean}
     */
    const [pageRendered, setPageRendered] = useState(false)
    
    const pdfLoading = useWireValue(store.pdfRendererLoading)
    
    const documentSearchResults = useWireValue(store.documentSearchResults)
    const pdfRendererPageCount = useWire(store.pdfRendererPageCount)
    
    //endregion State
    
    //region Helper Methods
    
    /**
     * Gets the matching terms for a given page.
     *
     * @param {Object} page - The page object.
     * @param {Boolean} recordIsParse - If the record is a Parse/county collection
     * @param {Array.<string>} highlightTerms - An array of highlight terms.
     * @returns {AnnotationResult[]} Returns an array of annotation results that match the highlight terms
     */
    const getMatchingTermsForPage = useCallback((recordIsParse, page, highlightTerms) => {
        
        const termMatchingFn = recordIsParse
            ? elasticUtils.getSearchIndexOcrDataForPage
            : elasticUtils.getSearchAnnotationResultsForPage
        
        // @todo Adding an empty result makes the search query still render, but
        // next merge will include better ocr matches with x/y/etc coords
        const rawOcrMatches = [{ text: [''] }]
        const annotationMatches = termMatchingFn(page, highlightTerms)
        
        return [
            ...rawOcrMatches,
            ...annotationMatches,
        ]
        
    }, [])
    
    //endregion Helper Methods
    
    //region Memos
    
    const page = useMemo(() => (
        record?.pages?.find(it => it.pageIndex === pageIndex)
    ), [record, pageIndex])
    
    const pngUrl = useMemo(() => (
        page?.fileName?.length > 0
            ? `${config.parseApiBaseUrl}/api/v1/pdfs/pngs/${page?.fileName}`
            : null
    ), [page])
    
    const pdfUrl = useMemo(() => buildRecordUrl(record, false, record?.collection?.isParse), [record])
    
    /**
     * Terms that are returned from the ElasticSearch query as highlights
     * include the full phrase they were matched in, so this extracts
     * just the matching terms
     * @type {String[]}
     */
    const highlightTerms = useMemo(() => {
        
        if (!documentSearchResults?.count || !documentSearchResults?.rows?.length) {
            console.warn('No terms found for search results', documentSearchResults)
            return []
        }
        
        const terms = documentSearchResults.rows.reduce((acc, row) => {
            
            for (const key in row.highlight) {
                
                row.highlight[key].forEach(phrase => {
                    
                    // Use a regular expression to extract content between <em> and </em> tags
                    const matches = phrase.match(/<em>(.*?)<\/em>/g)
                    
                    // If matches are found, add them to the phrases array
                    if (matches)
                        matches.forEach(match => {
                            
                            // Remove <em> and </em> tags and push the extracted content
                            const term = match
                                .replace(/<\/?em>/g, '')
                                .toLowerCase()
                            
                            acc[term] = true
                            
                        })
                    
                })
                
            }
            
            return acc
            
        }, {})
        
        // console.log('@bbox highlightTerms: found', terms)
        
        // @todo @debug not sure if filtering out uuids matters
        return Object.keys(terms)
            .filter(it => !looksLikeUuid(it))
        
    }, [documentSearchResults])
    
    /**
     * Memoized value that maps page indices to matching terms for each page.
     *
     * @returns {Object} result
     * @returns {Number} result.pageIndex The page index
     * @returns {AnnotationResult[]} result.matchingTerms The matching terms for the page
     */
    const resultsByPageIndex = useMemo(() => {
        
        return record?.pages?.reduce((acc, it) => ({
            ...acc,
            [it.pageIndex]: getMatchingTermsForPage(record.collection.isParse, it, highlightTerms),
        }), {})
        
    }, [record, highlightTerms])
    
    //endregion Memos
    
    //region Methods
    
    const setPageCount = useCallback(value => {
        
        pdfRendererPageCount.setValue(value)
        
    }, [pdfRendererPageCount])
    
    /**
     * Adjusts an annotation result's `x` & `width` properties to only cover
     * a bounding box that matches the term that was searched for
     * 
     * @param {Object} annotationResult Original annotation result
     * @param {Object} annotationResult.value
     * @param {Object} annotationResult.value.x Required x param
     * @param {Object} annotationResult.value.width Required width param
     * @param {String[]} annotationResult.value.text Required text param
     */
    const getSubstringHighlight = useCallback(annotationResult => {
        
        // Make a shallow copy of the annotation result
        const res = JSON.parse(JSON.stringify(annotationResult))
        
        // Result text is an array, so combine to one string
        const text = res.value?.text?.join(' ')?.toLowerCase() ?? ''
        
        // Calculate the width & starting point of a single character's bounding box
        // const charBoxX = res.value.x / text.length
        const charBoxWidth = res.value.width / text.length
        
        // Find the ElasticSearch highlighted term within our text payload
        const term = highlightTerms.find(it => text.includes(it))
        
        // We should always have a match, since the terms come from ElasticSearch,
        // but just in case, this would cause a crash, so bail early
        if (!term) return log.w('no term found', { highlightTerms, text })
        
        // Find the substring offset of the term within the annotation result text
        const idx = text.indexOf(term)
        
        // Adjust the width & starting point to only cover the matched term
        res.value.x = idx === 0 ? res.value.x : (res.value.x + (charBoxWidth * idx))
        res.value.width = charBoxWidth * term.length
        
        return res
        
    }, [highlightTerms])
    
    const renderOverlays = useCallback(() => {
        
        if (pdfLoading ||
            !pageRendered ||
            !record ||
            !resultsByPageIndex ||
            !refRenderCanvas.current ||
            !refDocumentCanvas.current ||
            !refOverlaysCanvas.current)
            return
        
        if (!record.pages?.length)
            return console.warn('No pages found', record)
        
        const results = resultsByPageIndex[pageIndex] ?? []
        const renderCanvas = refRenderCanvas.current
        const overlaysCanvas = refOverlaysCanvas.current
        const documentCanvas = refDocumentCanvas.current
        const renderCtx = renderCanvas.getContext('2d')
        const overlaysCtx = overlaysCanvas.getContext('2d')
        
        const debugPage = record.pages.find(it => it.pageIndex === pageIndex)
        
        console.log(
            'renderOverlays: overlaying with',
            highlightTerms.length, 'terms,',
            debugPage?.annotations?.length ?? 0,
            'annotations', results.length, 'results', {
                page: debugPage,
                results,
            },
        )
        
        // Initialize canvas widths/scaling/etc.
        canvasUtils.initSearchResultCanvases(documentCanvas, overlaysCanvas, renderCanvas)
        
        // Clear main & 2nd off-screen canvases
        renderCtx.clearRect(0, 0, documentCanvas.width, documentCanvas.height)
        overlaysCtx.clearRect(0, 0, documentCanvas.width, documentCanvas.height)
        
        // Copy the off-screen PDF-rendered canvas to our target canvas
        renderCtx.drawImage(documentCanvas, 0, 0)
        
        const validResultFields = ['x', 'y', 'width', 'height']
        
        // Render bounding boxes for all matches
        for (const result of results) {
            
            // @todo legacy
            if (!result.value)
                result.value = JSON.parse(JSON.stringify(result))
            
            const isValidResult = validResultFields.every(it =>
                Object.hasOwn(result?.value ?? {}, it))
            
            if (!isValidResult) {
                log.d('Invalid result', { result, results })
                continue
            }
            
            // Render a light highlight behind the whole matched annotation result phrase
            canvasUtils.renderSearchResultOverlay(
                renderCanvas,
                overlaysCanvas,
                documentCanvas,
                result,
                {
                    debug: false,
                    ...highlightThemes.classic,
                },
            )
            
        }
        
        // Draw the overlay canvas onto our target canvas
        renderCtx.drawImage(overlaysCanvas, 0, 0)
        
    }, [
        pdfLoading,
        pageRendered,
        record,
        pageIndex,
        resultsByPageIndex,
        getSubstringHighlight,
    ])
    
    //endregion Methods
    
    return {
        
        // Refs
        refRenderCanvas,
        refDocumentCanvas,
        refOverlaysCanvas,
        
        // State
        error,
        setError,
        record,
        setPageCount,
        pageRendered,
        setPageRendered,
        
        // Wires
        documentSearchResults,
        
        // Memos
        page,
        pngUrl,
        pdfUrl,
        highlightTerms,
        resultsByPageIndex,
        
        // Methods
        renderOverlays,
        
    }
    
}

export default DocumentViewerViewModel
